import {
  action,
  computed,
  IReactionDisposer,
  makeObservable,
  observable,
  reaction,
} from 'mobx';

import {
  CellControllerKind,
  SwitchControllerEntity,
} from 'entities/TableControllers';
import { debounce } from 'utils/debounce';

import { ITableControllerBase, TableControllerParams } from '../interface';

type PrivateFields = '_value';

const SWITCH_CHANGE_DEBOUNCE_TINE = 700;

export default class TableSwitchController
  implements
    ITableControllerBase<SwitchControllerEntity['controller']['value']>
{
  constructor({
    record,
    onSave,
  }: TableControllerParams<SwitchControllerEntity>) {
    this._value = record.controller;
    this._initialValue = record.controller;

    makeObservable<TableSwitchController, PrivateFields>(this, {
      _value: observable.ref,
      controllerValue: computed,
      setValue: action.bound,

      toServerFormat: computed,

      reset: action.bound,
    });

    const onSaveHandler = debounce(async () => {
      await onSave();
      this._isUpdateBlocked = false;
    }, SWITCH_CHANGE_DEBOUNCE_TINE);

    this._reactions.push(
      reaction(
        () => this._value,
        async () => {
          this._isUpdateBlocked = true;
          await onSaveHandler();
        }
      )
    );
  }

  private _value: SwitchControllerEntity['controller'];
  private readonly _reactions: IReactionDisposer[] = [];

  private readonly _initialValue: SwitchControllerEntity['controller'];

  private _isUpdateBlocked = false;

  get isUpdateBlocked(): boolean {
    return this._isUpdateBlocked;
  }

  get controllerValue() {
    return Boolean(this._value.value);
  }

  get controllerKind() {
    return CellControllerKind.SWITCH;
  }

  get toServerFormat() {
    return this._value.value;
  }

  reset(): void {
    this._value = this._initialValue;
  }

  destroy() {
    this._reactions.forEach((reaction) => reaction());
  }

  setValue(nextValue: boolean): void {
    if (nextValue === this._value.value) {
      return;
    }

    this._value = {
      value: nextValue,
    };
  }
}
